import * as React from "react";

import "./agent-display-fields.scss";

import {cs}                             from "@Common/react/chain-services";
import {consumeContext}                 from "@Common/react/context";
import {fragments}                      from "@Common/react/fragments";
import {Invoke}                         from "@Common/react/invoke";
import {keyed}                          from "@Common/react/keyed";
import {scopedState}                    from "@Common/react/scoped-state";
import {scnv}                           from "@Common/react/state-change-nested-value";
import {UseState}                       from "@Common/react/use-state";
import {ConfirmDialog}                  from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {setCurrentToSaved}              from "@Common/ui-components/heavy/item-options/modules/set-current-to-saved";
import {cnv}                            from "@Common/utils/arr-path";
import {chain}                          from "@Common/utils/fs";
import {equalDeep, filterKeys, isEmpty} from "@Common/utils/objects";

import {FieldLine}   from "../common/field-line";
import {Textarea5ew} from "../common/textarea-5ew";

export const AgentDisplayFields = ({initEnrollment, agentId, next}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    ["acceptField", ({company}, next) => next((i) => company?.displayFields?.hasOwnProperty("" + i))],
    ["extractProfile", ({acceptField}, next) => next((data) => extractProfile(data, {
        acceptField,
    }))],
    ["profile", ({extractProfile}, next) => UseState({
        getInitValue: () => chain(
            !initEnrollment?.profile ? null : extractProfile(initEnrollment.profile),
            (_) => ({
                current: {data:_},
                saved  : {data:_},
            })
        ),
        next,
    })],
    ["getResetData", ({apis, extractProfile}, next) => next(async () => {
        return extractProfile(await apis.next.session.getAgent({agentId}));
    })],
    ({getResetData, profile}, next) => fragments(
        next(),

        !profile.value.current.data && Invoke({
            action: async () => {
                const resetData = await getResetData();
                scnv(profile, ["current"], () => ({data: resetData}));
            },
        })
    ),
    ["confirmDialog", ({}, next) => ConfirmDialog({next})],
    ["editable", ({company}, next) => next(!!company?.prefs?.agentEditDisplay)],
    ({profile, getResetData, confirmDialog, acceptField, editable, company}) => next({
        enrollmentEditor: {
            $name: "Information",
            prepareSave: (enrollment) => ({
                newValue: cnv(enrollment, ["profile"], (_) => ({
                    ...clearProfile(_),
                    ...profile.value.current.data,
                })),
                onSaved: () => setCurrentToSaved(profile),
            }),
            changed: !equalDeep(profile.value.saved, profile.value.current),
            needUrgentSaving: profile.value.current.manual,
            loading: !profile.value.current.data,
            valid: profile.value.current.data,
        },
        resetToDefault: async () => {
            const confirm = await confirmDialog.show({
                actionName: "update",
                message: "Update info with default profile?",
            })
            if (!confirm) {
                return;
            }
            const resetData = await getResetData();
            scnv(profile, ["current", "data"], () => resetData);
        },
        render: ({}) => cs(

            ({}) => (
                <div className="agent-display-fields-4ar">
                    {acceptField(1) && (
                        <div className="header">
                            {Textarea5ew({
                                editable,
                                className: "name",
                                state: scopedState(profile, ["current", "data", agentNameLine.attr]),
                                placeholder: agentNameLine.label,
                                maxLength  : agentNameLine.maxLength,
                                xdQaName   : agentNameLine.label,
                            })}
                        </div>
                    )}
                    <div className="lines">
                        {lines.map(({label, attr, maxLength}, i) => cs(
                            keyed(i),
                            ({}, next) => acceptField(i+2) && next(),
                            ["label1", (_, next) => next(company?.displayFields?.["" + (i+2)])],
                            ({label1}) => FieldLine({
                                state: scopedState(profile, ["current", "data", attr]),
                                editable,
                                label: label1 ?? label,
                                maxLength,
                            }),
                        ))}
                    </div>
                </div>
            )
        ),
    }),
);


const agentNameLine = {
    label: "Agent name",
    attr: "agentDisplay1",
    maxLength: 150,
};
const lines = [
    {
        label: "Title",
        attr: "agentDisplay2",
        "maxLength": 125, // 60,
    },
    {
        label: "Email",
        attr: "agentDisplay3",
        "maxLength": 125, // 60,
    },
    {
        label: "Phone 1",
        attr: "agentDisplay4",
        "maxLength": 125, // 25,
    },
    {
        label: "Phone 2",
        attr: "agentDisplay5",
        "maxLength": 125, // 25,
    },
    {
        label: "Phone 3",
        attr: "agentDisplay6",
        "maxLength": 125, // 25,
    },
    {
        label: "Website",
        attr: "agentDisplay7",
        "maxLength": 125,
    },
    {
        label: "Social Media",
        attr: "agentDisplay8",
        "maxLength": 125,
    },
];

const extractProfile = (_, {acceptField}) => chain(
    filterKeys(_, (k:string) => {
        const m = /^agentDisplay(\d)$/.exec(k);
        return !!m && acceptField(+m[1]);
    }),
    (_) => isEmpty(_) ? null : _,
);
const clearProfile = (_) => filterKeys(_??{}, (k:string) => !/^agentDisplay\d$/.test(k));

